import React, { useEffect, useState } from 'react';
import { getNominas, calcularNomina, pagarNomina, getEmpleados, getResumenNomina } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { Plus, Calculator, DollarSign, CheckCircle, Clock, Users } from 'lucide-react';

const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-DO', {
        style: 'currency',
        currency: 'DOP',
        minimumFractionDigits: 2,
    }).format(value);
};

export default function NominaPage() {
    const [nominas, setNominas] = useState([]);
    const [empleados, setEmpleados] = useState([]);
    const [resumen, setResumen] = useState(null);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [periodoActual, setPeriodoActual] = useState(new Date().toISOString().slice(0, 7));
    const [form, setForm] = useState({
        empleado_id: '',
        periodo: periodoActual,
        dias_trabajados: '30',
        horas_extra: '0',
        bonificaciones: '0',
        deducciones: '0',
        notas: '',
    });

    useEffect(() => {
        loadData();
    }, [periodoActual]);

    const loadData = async () => {
        try {
            const [nominasRes, empleadosRes] = await Promise.all([
                getNominas({ periodo: periodoActual }),
                getEmpleados({ estado: 'activo' })
            ]);
            setNominas(nominasRes.data);
            setEmpleados(empleadosRes.data);
            
            try {
                const resumenRes = await getResumenNomina(periodoActual);
                setResumen(resumenRes.data);
            } catch (e) {
                setResumen(null);
            }
        } catch (error) {
            toast.error('Error al cargar datos');
        } finally {
            setLoading(false);
        }
    };

    const handleCalcular = async (e) => {
        e.preventDefault();
        try {
            const data = {
                ...form,
                dias_trabajados: parseInt(form.dias_trabajados),
                horas_extra: parseFloat(form.horas_extra),
                bonificaciones: parseFloat(form.bonificaciones),
                deducciones: parseFloat(form.deducciones),
            };
            await calcularNomina(data);
            toast.success('Nómina calculada');
            setDialogOpen(false);
            resetForm();
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al calcular nómina');
        }
    };

    const handlePagar = async (nominaId) => {
        try {
            await pagarNomina(nominaId);
            toast.success('Nómina pagada y registrada como gasto');
            loadData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Error al pagar nómina');
        }
    };

    const resetForm = () => {
        setForm({
            empleado_id: '',
            periodo: periodoActual,
            dias_trabajados: '30',
            horas_extra: '0',
            bonificaciones: '0',
            deducciones: '0',
            notas: '',
        });
    };

    const empleadosSinNomina = empleados.filter(
        e => !nominas.find(n => n.empleado_id === e.id)
    );

    if (loading) {
        return (
            <div className="space-y-6">
                <Skeleton className="h-10 w-48" />
                <Skeleton className="h-96" />
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="nomina-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="font-heading text-2xl sm:text-3xl font-bold">Nómina</h1>
                    <p className="text-muted-foreground mt-1">Gestión de pagos a empleados</p>
                </div>
                <div className="flex gap-2">
                    <Input
                        type="month"
                        value={periodoActual}
                        onChange={(e) => setPeriodoActual(e.target.value)}
                        className="w-40"
                    />
                    <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                        <DialogTrigger asChild>
                            <Button onClick={resetForm} data-testid="calcular-nomina-btn">
                                <Calculator className="w-4 h-4 mr-2" />
                                Calcular Nómina
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle className="font-heading">Calcular Nómina</DialogTitle>
                            </DialogHeader>
                            <form onSubmit={handleCalcular} className="space-y-4">
                                <div className="space-y-2">
                                    <Label>Empleado</Label>
                                    <Select
                                        value={form.empleado_id}
                                        onValueChange={(value) => setForm({ ...form, empleado_id: value })}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Seleccionar empleado" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {empleadosSinNomina.map((emp) => (
                                                <SelectItem key={emp.id} value={emp.id}>
                                                    {emp.nombre} - {formatCurrency(emp.salario_base)}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Período</Label>
                                        <Input
                                            type="month"
                                            value={form.periodo}
                                            onChange={(e) => setForm({ ...form, periodo: e.target.value })}
                                            required
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Días Trabajados</Label>
                                        <Input
                                            type="number"
                                            value={form.dias_trabajados}
                                            onChange={(e) => setForm({ ...form, dias_trabajados: e.target.value })}
                                            required
                                            min="0"
                                            max="31"
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>Horas Extra</Label>
                                        <Input
                                            type="number"
                                            value={form.horas_extra}
                                            onChange={(e) => setForm({ ...form, horas_extra: e.target.value })}
                                            min="0"
                                            step="0.5"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Bonificaciones</Label>
                                        <Input
                                            type="number"
                                            value={form.bonificaciones}
                                            onChange={(e) => setForm({ ...form, bonificaciones: e.target.value })}
                                            min="0"
                                        />
                                    </div>
                                </div>
                                <div className="space-y-2">
                                    <Label>Otras Deducciones</Label>
                                    <Input
                                        type="number"
                                        value={form.deducciones}
                                        onChange={(e) => setForm({ ...form, deducciones: e.target.value })}
                                        min="0"
                                    />
                                </div>
                                <div className="flex justify-end gap-2 pt-4">
                                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                        Cancelar
                                    </Button>
                                    <Button type="submit" disabled={!form.empleado_id}>
                                        Calcular
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            {/* Resumen */}
            {resumen && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-primary/10">
                                    <Users className="w-5 h-5 text-primary" />
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Empleados</p>
                                    <p className="text-xl font-bold">{resumen.total_empleados}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-accent/10">
                                    <DollarSign className="w-5 h-5 text-accent" />
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Total Neto</p>
                                    <p className="text-xl font-bold font-mono">{formatCurrency(resumen.total_neto)}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-green-500/10">
                                    <CheckCircle className="w-5 h-5 text-green-500" />
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Pagadas</p>
                                    <p className="text-xl font-bold font-mono">{formatCurrency(resumen.monto_pagado)}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                    <Card>
                        <CardContent className="p-4">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-warning/10">
                                    <Clock className="w-5 h-5 text-warning" />
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Pendientes</p>
                                    <p className="text-xl font-bold font-mono">{formatCurrency(resumen.monto_pendiente)}</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            )}

            {/* Table */}
            <Card>
                <CardHeader>
                    <CardTitle className="font-heading text-lg">Nóminas del Período {periodoActual}</CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Empleado</TableHead>
                                    <TableHead>Cargo</TableHead>
                                    <TableHead className="text-right">Bruto</TableHead>
                                    <TableHead className="text-right">Deducciones</TableHead>
                                    <TableHead className="text-right">Neto</TableHead>
                                    <TableHead>Estado</TableHead>
                                    <TableHead className="w-20"></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {nominas.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} className="text-center py-12">
                                            <Calculator className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
                                            <p className="text-muted-foreground">No hay nóminas calculadas para este período</p>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    nominas.map((nomina) => (
                                        <TableRow key={nomina.id} className="animate-fadeIn">
                                            <TableCell>
                                                <div>
                                                    <p className="font-medium">{nomina.empleado_nombre}</p>
                                                    <p className="text-xs text-muted-foreground">{nomina.empleado_cedula}</p>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <p className="text-sm">{nomina.cargo}</p>
                                                <p className="text-xs text-muted-foreground">{nomina.departamento}</p>
                                            </TableCell>
                                            <TableCell className="text-right font-mono">
                                                {formatCurrency(nomina.total_bruto)}
                                            </TableCell>
                                            <TableCell className="text-right font-mono text-destructive">
                                                -{formatCurrency(nomina.total_deducciones)}
                                            </TableCell>
                                            <TableCell className="text-right font-mono font-bold text-primary">
                                                {formatCurrency(nomina.neto_pagar)}
                                            </TableCell>
                                            <TableCell>
                                                <Badge 
                                                    variant="outline" 
                                                    className={nomina.estado === 'pagada' 
                                                        ? 'bg-accent/10 text-accent' 
                                                        : 'bg-warning/10 text-warning'}
                                                >
                                                    {nomina.estado === 'pagada' ? 'Pagada' : 'Pendiente'}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                {nomina.estado !== 'pagada' && (
                                                    <Button
                                                        size="sm"
                                                        onClick={() => handlePagar(nomina.id)}
                                                        data-testid={`pagar-nomina-${nomina.id}`}
                                                    >
                                                        Pagar
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
